import React, { useMemo, useState } from 'react';
import { TickerType } from '../types';
import TickerName from 'components/TickerName';
import Link from 'components/Link';
import { isValidUSStockSymbol } from 'utils/shared/tickers/validation';

interface TickerCellProps {
  ticker: string;
  tickerData?: TickerType[];
  slug?: string;
}

const imageStatusCache = new Map<string, 'success' | 'error'>();

export const TickerCell: React.FC<TickerCellProps> = ({ ticker, tickerData = [], slug }) => {
  const [imageError, setImageError] = useState(false);

  const companyData = useMemo(() => {
    const foundTicker = tickerData.find((item) => item.t === ticker);
    return {
      symbol: foundTicker?.t || ticker,
      name: foundTicker?.n || ticker,
      imageUrl: `https://images.financialmodelingprep.com/symbol/${ticker}.png`,
      hasData: !!foundTicker,
    };
  }, [ticker, tickerData]);

  const shouldAttemptImageLoad = useMemo(() => {
    const cachedStatus = imageStatusCache.get(ticker);

    if (cachedStatus === 'error') return false;
    if (cachedStatus === 'success') return true;

    return isValidUSStockSymbol(ticker);
  }, [ticker]);

  const handleImageError = () => {
    setImageError(true);
    imageStatusCache.set(ticker, 'error');
  };

  const handleImageLoad = () => {
    setImageError(false);
    imageStatusCache.set(ticker, 'success');
  };

  return (
    <div className="flex items-center gap-2">
      {shouldAttemptImageLoad && !imageError ? (
        <img
          src={companyData.imageUrl}
          alt={ticker}
          className="h-5 w-5 rounded-full"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      ) : (
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-600">
          {ticker?.charAt(0)?.toUpperCase() || '•'}
        </div>
      )}

      <Link href={`${slug}/${ticker}`}>
        <TickerName name={companyData.name}>{companyData.symbol}</TickerName>
      </Link>
    </div>
  );
};
